import React, {useLayoutEffect, useState, useEffect, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';

import DashboardActiveHeader from './components/header/header';
import DashboardActiveMain from './components/main/main';
import DashboardActiveFooter from './components/footer/footer';

import DDU from './components/ddu/ddu'

import {getInitialTelemetry, getInitialLeaderboard} from './general/shemas';

const INITIAL_TELEMETRY = getInitialTelemetry()
const INITIAL_LEADERBOARD = getInitialLeaderboard()

async function checkUserSession() {
    const response = await fetch('/api/check-token', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "include",
    });

    if (response.ok) {
        return true;
    } else {
        return false;
    }
}

async function checkUserDeviceConnected() {
    const response = await fetch('/api/check-user-device-conneted', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "include",
    });

    const status = await response.json();
    return status[0];
}

async function getUserUUID() {
    const response = await fetch('/api/get-account-uuid', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "include",
    });

    if (response.ok) {
        const user_data = await response.json();
        return user_data;
    } else {
        return false;
    }
}

async function getUserTelemetryCode(uuid) {
    if (uuid === undefined) {return}
    const response = await fetch('/api/get-telemetry-code', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
            user_uuid: uuid,
        })
    });

    if (response.ok) {
        const user_data = await response.json();
        return user_data;
    } else {
        return false;
    }
}

async function getUserData() {
    const response = await fetch('/api/get-account-data', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "include",
    });

    if (response.ok) {
        const user_data = await response.json();
        return user_data;
    } else {
        return false;
    }
}

async function setUserOnline() {
    const response = await fetch('/api/presence', {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        credentials: "include",
    });

    if (response.ok) {
        return true;
    } else {
        return false;
    }
}

function Dashboard() {
    const navigate = useNavigate();
    const [isReady, setIsReady] = useState(false);
    const [telemetry, setTelemetry] = useState(INITIAL_TELEMETRY);
    const [leaderboard, setLeaderboard] = useState(INITIAL_LEADERBOARD);
    const [currentDriver, setCurrentDriver] = useState("You")
    const [currentDriverUUID, setCurrentDriverUUID] = useState("You")
    const [currentDriverTelemetryCode, setCurrentDriverTelemetryCode] = useState("")

    const [userUUID, setUserUUID] = useState(undefined)
    const [userData, setUserData] = useState({})

    const [sessionSaved, setSessionSaved] = useState(false)
    const sessionSavedRef = useRef(sessionSaved);

    const [clientSessionSaved, setClientSessionSaved] = useState(false)
    const [clientSessionSavedText, setClientSessionSavedText] = useState("")

    const [isGameRunning, setIsGameRunning] = useState(false)
    const [udpFeedStatus, setUdpFeedStatus] = useState(false)

    const [sessionType, setSessionType] = useState(0)
    const sessionTypeRef = useRef(sessionType);

    const [sessionTrack, setSessionTrack] = useState("")
    const [sessionCar, setSessionCar] = useState("")
    const [sessionLaps, setSessionLaps] = useState(0)
    const [sessionUserBestLap, setSessionUserBestLap] = useState("")
    const [sessionTotalDistance, setSessionTotalDistance] = useState(0)

    const [changeHUD, setChangeHUD] = useState(false)

    const [showAlertOK, setShowAlertOK] = useState(false)
    const [OKAlertText, setOKAlertText] = useState("")

    const [showAlertERROR, setShowAlertERROR] = useState(false)
    const [ERRORAlertText, setERRORAlertText] = useState("")

    const showAlert = (type, text) => {
        if (type === "error") {
            setShowAlertERROR(true)
            setERRORAlertText(text)

            setTimeout(function() {
                setShowAlertERROR(false)
            }, 3500)
        } else if (type = "ok") {
            setShowAlertOK(true)
            setOKAlertText(text)

            setTimeout(function() {
                setShowAlertOK(false)
            }, 3500)
        }
    }

    useLayoutEffect(() => {
        const startDashboard = async () => {
            if (!await checkUserSession()) {
                navigate('/login');
            } else if (!await checkUserDeviceConnected()) {
                navigate('/dashboard-error')
            }

            setUserData(await getUserData())

            if (currentDriver === "You") {
                const user_uuid = await getUserUUID()
                setCurrentDriverUUID(user_uuid)
                setUserUUID(user_uuid)

                if (user_uuid !== undefined) {
                    const user_telemetry_code = await getUserTelemetryCode(user_uuid);
                    setCurrentDriverTelemetryCode(user_telemetry_code);
                }
            }

            setIsReady(true);
        };

        if (!isReady) {
            startDashboard();
        }
        
    }, [navigate, currentDriver, isReady])

    useEffect(() => {
        if (currentDriver && isReady) {
            async function fetchDriverData() {
                const driver_telemetry_code = await getUserTelemetryCode(currentDriverUUID)

                setCurrentDriverTelemetryCode(driver_telemetry_code)
                setTelemetry(INITIAL_TELEMETRY)
            }

            fetchDriverData();
        }
    }, [currentDriver, currentDriverUUID, isReady])

    useEffect(() => {
        sessionSavedRef.current = sessionSaved;
    }, [sessionSaved]);

    useEffect(() => {
        sessionTypeRef.current = sessionType;
    }, [sessionType]);

    useEffect(() => {
        let ws;
        let isStopped = false;

        const formatGear = (gearValue) => {
            const gear = Number(gearValue);

            if (gear === 0) return "R";
            if (gear === 1) return "N";
            
            return gear - 1;
        };

        if (currentDriverTelemetryCode === undefined || !currentDriverTelemetryCode) {return}

        const connect = () => {
            if (isStopped) return;
            
            ws = new WebSocket('wss://live-telemetry.gmigliaro.it/ws/telemetry/' + currentDriverTelemetryCode);

            ws.onmessage = (event) => {
                if (isStopped) return;
                const payload = JSON.parse(event.data);
                const data = payload.data;

                setUdpFeedStatus(true)

                if (payload.type === 1) {
                    if (data["session-status"] !== 0) {  
                        data.gear = formatGear(data.gear)
                        data["time-left"] = data["time-left"] === -1 ? "∞" : data["time-left"];
                        data["delta-lap-time"] = data["delta-lap-time"] === "-:--:---" ? "0" : data["delta-lap-time"];
                        data["is-delta-positive"] = data["is-delta-positive"] === 1 ? "+" : "-";
                        data["best-time"] = data["best-time"] === "35791:23" ? "-:--:---" : data["best-time"];
                        data["last-time"] = data["last-time"] === "35791:23" ? "-:--:---" : data["last-time"];
                        data["estimated-lap"] = data["estimated-lap"] === "35791:23" ? "-:--:---" : data["estimated-lap"];
                        
                        setTelemetry(data);
                        
                        if (sessionTypeRef.current !== data["session-type"] && sessionSavedRef.current) {
                            setSessionType(data["session-type"])
                            setSessionSaved(false)
                        } else if (sessionTypeRef.current === 0) {
                            setSessionType(data["session-type"])
                        }
                
                        setSessionTrack(data.track)
                        setSessionCar(data["car-name"])

                        if (parseInt(data["completed-laps"]) !== 0) {
                            setSessionLaps(data["completed-laps"])
                        }

                        setSessionUserBestLap(data["best-time"])

                        if (parseInt(data["distance-travelled"]) > 500) {
                            setSessionTotalDistance(parseInt(data["distance-travelled"]))
                        }

                        setIsGameRunning(true)
                    }
                } else if (payload.type === 2) {
                    setLeaderboard(data)
                } else if (payload.type === 3) {
                    /*setSaveCurrentSession(true)*/
                } else if (payload.type === 4) {
                    setUdpFeedStatus(false)
                    setIsGameRunning(false)

                    /*setSaveCurrentSession(true)*/
                    isStopped = true
                } else if (payload.type === 5) {
                    setIsGameRunning(true)
                } else if (payload.type === 6) {
                    setClientSessionSaved(true)
                    setClientSessionSavedText("Session saved successfully.")
                } else if (payload.type === 7) {
                    setClientSessionSaved(true)
                    setClientSessionSavedText("Error while saving the session.")
                } else if (payload.type === 10) {
                    setChangeHUD(true)
                }
            }
            
            ws.onclose = (e) => {
                if (!isStopped) {
                    setUdpFeedStatus(false)
                    setTimeout(connect, 1000);
                }
            }
        };

        connect();

        return () => {
            isStopped = true;
            setUdpFeedStatus(false)
            if (ws) ws.close();
        };
    }, [currentDriverTelemetryCode]);

    useEffect(() => {
        if (telemetry) {
            console.log("🏎️ Stato aggiornato:", telemetry);
        }
    }, [telemetry]);

    useEffect(() => {
        if (leaderboard) {
            /*console.log("🏎️ Leaderboard aggiornata:", leaderboard);*/
        }
    }, [leaderboard]);

    useEffect(() => {
        if (clientSessionSaved && currentDriverUUID === userUUID) {
            if (clientSessionSavedText == "Session saved successfully.") {
                showAlert("ok", "Session saved successfully.")
            } else {
                showAlert("error", "Error while saving the session.")
            }

            setClientSessionSaved(false)
        }
    }, [clientSessionSaved, clientSessionSavedText, currentDriverUUID, userUUID])

    useEffect(() => {
        const interval = setInterval(async () => {
            await setUserOnline()
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    const requestWakeLock = useCallback(async () => {
        if ('wakeLock' in navigator) {
            try {
                const lock = await navigator.wakeLock.request('screen');

                lock.addEventListener('release', () => {});

                return lock;
            } catch (err) {}
        }
        return null;
    }, []);

    useEffect(() => {
        let sentinel = null;

        const initLock = async () => {
        sentinel = await requestWakeLock();
        };

        const handleVisibilityChange = async () => {
        if (sentinel !== null && document.visibilityState === 'visible') {
            sentinel = await requestWakeLock();
        }
        };

        initLock();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (sentinel) sentinel.release();
        };
    }, [requestWakeLock]);

    if (isReady) {
        return (
            <>
                <div className="dashboard-error flex-col items-center content-center">
                    <h1>Ops!</h1>
                    <p className="w-[95%] text-center">This platform is only available on PC or on landscape mode for optimal graph viewing.</p>
                </div>

                <div className={`${telemetry["pit-limiter"] === 1 ? "bg-[#0055ffbd]" : "bg-transparent"} ddu w-full h-[100vh] relative flex-col items-center justify-center`}>
                    <DDU value={telemetry} userData={userData} changeHUD={changeHUD} setChangeHUD={setChangeHUD} leaderboard={leaderboard}/>
                </div>

                <div className="dashboard">
                    <div className="relative flex h-screen w-full flex-col overflow-hidden">
                        <DashboardActiveHeader value={telemetry} setCurrentDriver={setCurrentDriver} currentDriver={currentDriver} setCurrentDriverUUID={setCurrentDriverUUID} userData={userData}/>
                        <DashboardActiveMain value={telemetry} userData={userData} leaderboard={leaderboard}/>
                        <DashboardActiveFooter isGameRunning={isGameRunning} userData={userData} udpFeedStatus={udpFeedStatus}/>
                    </div>
                </div>

                {showAlertOK && <span className="flex px-5 py-2 rounded-xl fixed z-[999] bg-alert-green text-sm right-[10px] top-[10px] select-none animate-[show-alert_3s_ease-in-out_forwards]">{OKAlertText}</span>}
                {showAlertERROR && <span className="flex px-5 py-2 rounded-xl fixed z-[999] bg-alert-red text-sm right-[10px] top-[10px] select-none animate-[show-alert_3s_ease-in-out_forwards]">{ERRORAlertText}</span>}
            </>
        );
    }
}


export default Dashboard;